import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, CreditCard, Loader2, Lock, Plus, Trash2 } from "lucide-react";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { sharedApi } from "@/lib/api";

/**
 * Iter-597/598 — saved cards manager. Cards are saved by ticking
 * "Save my card for faster checkout" on Stripe's hosted payment page,
 * or added directly via the hosted setup page (no charge).
 *   GET    /api/payments/methods                → {cards: [...]}
 *   POST   /api/payments/methods/setup          {wallet, return_url} → {url}
 *   POST   /api/payments/methods/{pm_id}/default
 *   DELETE /api/payments/methods/{pm_id}        → {removed: true}
 */
const brandLabel = (b) =>
  ({
    visa: "Visa",
    mastercard: "Mastercard",
    amex: "American Express",
    discover: "Discover",
    jcb: "JCB",
    unionpay: "UnionPay",
    diners: "Diners",
  }[String(b || "").toLowerCase()] || (b ? b.charAt(0).toUpperCase() + b.slice(1) : "Card"));

export default function PaymentMethodsPage() {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(null); // card object | null
  const [addOpen, setAddOpen] = useState(false);
  // Iter-598 — Stripe setup returns here with ?card_added=1.
  const [urlParams] = useSearchParams();
  const cardAdded = urlParams.get("card_added") === "1";

  const cardsQ = useQuery({
    queryKey: ["payment-methods"],
    retry: 0,
    queryFn: async () => (await sharedApi.get("/payments/methods")).data?.cards || [],
  });

  // Iter-598 — "Add a card": hosted Stripe setup page (no charge).
  const setupMut = useMutation({
    mutationFn: async (wallet) =>
      (
        await sharedApi.post("/payments/methods/setup", {
          wallet,
          return_url: `${window.location.origin}/account/payment-methods`,
        })
      ).data,
    onSuccess: (data) => {
      if (data?.url) window.location.assign(data.url);
      else toast.error("Couldn't open the add-card page — please try again.");
    },
    onError: () => toast.error("Couldn't open the add-card page — please try again."),
  });

  // Iter-598 — default card per wallet.
  const defaultMut = useMutation({
    mutationFn: async (id) => (await sharedApi.post(`/payments/methods/${id}/default`)).data,
    onSuccess: () => {
      toast.success("Default card updated.");
      qc.invalidateQueries({ queryKey: ["payment-methods"] });
    },
    onError: () => toast.error("Couldn't set the default card — please try again."),
  });

  const removeMut = useMutation({
    mutationFn: async (id) => (await sharedApi.delete(`/payments/methods/${id}`)).data,
    onSuccess: () => {
      toast.success("Card removed.");
      setConfirming(null);
      qc.invalidateQueries({ queryKey: ["payment-methods"] });
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't remove the card — please try again.");
    },
  });

  const cards = cardsQ.data || [];

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="payment-methods-page">
      <Seo title="Payment methods · Allsale Classified" />
      <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Account", href: "/account" }, { name: "Payment methods" }]} />

      <h1 className="mt-4 font-display text-3xl md:text-4xl font-bold text-slate-900">Payment methods</h1>
      <p className="mt-2 text-sm text-slate-500">Saved cards for faster store-bill checkout.</p>

      {cardAdded && (
        <div
          className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm font-bold text-emerald-700"
          data-testid="payment-methods-added-banner"
        >
          ✅ Card added — it will be pre-filled at checkout.
        </div>
      )}

      {/* Iter-598 — Add a card (choose which checkout it's for) */}
      <div className="mt-4">
        <button
          type="button"
          data-testid="payment-methods-add-btn"
          disabled={setupMut.isPending}
          onClick={() => setAddOpen((o) => !o)}
          className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:opacity-95 text-white font-bold px-5 py-2.5 text-sm shadow-card disabled:opacity-60"
        >
          {setupMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add a card
        </button>
        {addOpen && !setupMut.isPending && (
          <div className="mt-2 grid sm:grid-cols-2 gap-2" data-testid="payment-methods-add-chooser">
            <button
              type="button"
              data-testid="payment-methods-add-store"
              onClick={() => {
                setAddOpen(false);
                setupMut.mutate("store");
              }}
              className="rounded-2xl bg-white border border-soft hover:border-[color:var(--brand-blue)] text-left px-4 py-3 transition-colors"
            >
              <div className="text-sm font-bold text-slate-900">🏪 For store bills</div>
              <div className="text-xs text-slate-500 mt-0.5">Engagement / partner store payments</div>
            </button>
            <button
              type="button"
              data-testid="payment-methods-add-bazaar"
              onClick={() => {
                setAddOpen(false);
                setupMut.mutate("bazaar");
              }}
              className="rounded-2xl bg-white border border-soft hover:border-[color:var(--brand-blue)] text-left px-4 py-3 transition-colors"
            >
              <div className="text-sm font-bold text-slate-900">🛍️ For Bazaar shop</div>
              <div className="text-xs text-slate-500 mt-0.5">Indian Bazaar order payments</div>
            </button>
          </div>
        )}
      </div>

      <section className="mt-6 rounded-3xl bg-white shadow-card p-6">
        {cardsQ.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="payment-methods-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your cards…
          </div>
        ) : cards.length === 0 ? (
          <div className="text-center py-6" data-testid="payment-methods-empty">
            <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-400 grid place-items-center mx-auto">
              <CreditCard className="w-6 h-6" />
            </div>
            <p className="mt-3 text-sm text-slate-600 max-w-sm mx-auto">
              No saved cards yet — next time you pay a store bill or shop order, tick{" "}
              <span className="font-semibold">&quot;Save my card for faster checkout&quot;</span> on the payment
              page, or add one now with the button above.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="payment-methods-list">
            {cards.map((c) => (
              <li key={c.id} className="flex items-center gap-4 py-3.5" data-testid={`payment-method-${c.id}`}>
                <div className="w-11 h-11 rounded-2xl bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] grid place-items-center shrink-0">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900">
                      {brandLabel(c.brand)} •••• {c.last4}
                    </span>
                    {c.is_default && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-black px-2 py-0.5"
                        data-testid={`payment-method-default-${c.id}`}
                      >
                        <CheckCircle2 className="w-3 h-3" /> Default
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Expires {String(c.exp_month).padStart(2, "0")}/{c.exp_year}
                    {c.wallet ? ` · ${c.wallet === "store" ? "Store bills" : "Bazaar shop"}` : ""}
                  </div>
                  {!c.is_default && (
                    <button
                      type="button"
                      data-testid={`payment-method-setdefault-${c.id}`}
                      disabled={defaultMut.isPending}
                      onClick={() => defaultMut.mutate(c.id)}
                      className="mt-1 text-xs font-bold text-[color:var(--brand-blue)] hover:underline disabled:opacity-60"
                    >
                      Set as default
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  aria-label={`Remove ${brandLabel(c.brand)} ending ${c.last4}`}
                  data-testid={`payment-method-remove-${c.id}`}
                  onClick={() => setConfirming(c)}
                  className="w-9 h-9 rounded-full grid place-items-center text-red-500 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="mt-4 flex items-start gap-2 text-xs text-slate-500" data-testid="payment-methods-security-note">
        <Lock className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        🔒 Card numbers are stored securely by Stripe — AllSale never sees or stores your full card details.
      </p>

      {confirming && (
        <div
          className="fixed inset-0 z-[65] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !removeMut.isPending && setConfirming(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            className="w-full max-w-sm rounded-3xl bg-white shadow-xl p-5"
            data-testid="payment-method-confirm-modal"
          >
            <div className="font-display font-bold text-slate-900">
              Remove {brandLabel(confirming.brand)} •••• {confirming.last4}?
            </div>
            <p className="mt-2 text-sm text-slate-600">
              This can&apos;t be undone — you can save it again next time you pay.
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(null)}
                disabled={removeMut.isPending}
                data-testid="payment-method-confirm-cancel"
                className="rounded-full border border-soft bg-white hover:bg-slate-50 text-slate-700 font-semibold px-5 py-2 text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => removeMut.mutate(confirming.id)}
                disabled={removeMut.isPending}
                data-testid="payment-method-confirm-remove"
                className="rounded-full bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-semibold px-5 py-2 text-sm inline-flex items-center gap-2"
              >
                {removeMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Remove card
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
